import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { router } from './app/router.js';
import { initTheme } from './app/themeStore.js';
import { initTextScale } from './app/textScaleStore.js';
import { initTrail } from './features/nav/trailStore.js';
import { startDeclination } from './features/nav/declinationStore.js';
import { initConditions } from './features/conditions/conditionsController.js';
import { startPositionWatch } from './features/nav/positionService.js';
import { startRecorderService } from './features/tracks/recorderService.js';
import { initShareService } from './features/share/shareService.js';
import { registerServiceWorker } from './features/offline/serviceWorker.js';
import { useAuthStore } from './features/account/authStore.js';
import { startSyncTriggers } from './features/account/syncService.js';
import { startSettingsSync } from './features/account/settingsSync.js';
import { useBaseLayerStore } from './features/map/baseLayerStore.js';
import { useAisSourcesStore } from './features/ais/aisSourcesStore.js';
import { initAnalytics } from './lib/analytics.js';
import './app/global.css';

initAnalytics();
initTheme();
initTextScale();
// Position + recording run app-wide while the app is open (spec 03): trips log
// themselves regardless of which screen is showing.
startPositionWatch();
startRecorderService();
void initShareService();
initTrail();
startDeclination();
initConditions();
registerServiceWorker();
// Mirrors the six device-local pref stores onto the sync engine (spec 17).
// Safe pre-login: writes to IDB are harmless, and the sync trigger it pokes
// is already a no-op while logged out (spec 08).
startSettingsSync();

// Resolve the session (anonymous-first — never blocks the UI), then arm sync.
void useAuthStore
  .getState()
  .refresh()
  .finally(() => startSyncTriggers());

// Discover available base charts (e.g. Skippo, if the server has it configured).
void useBaseLayerStore.getState().loadSources();

// AIS toggle is only shown when the server has BarentsWatch credentials (spec 16).
void useAisSourcesStore.getState().load();

// Dev-only: inject synthetic fixes to exercise the recorder without a GPS.
if (import.meta.env.DEV) {
  void import('./features/nav/posStore.js').then(({ posStore }) => {
    (window as unknown as { __los: unknown }).__los = {
      pushFix: (fix: unknown) => posStore.getState().setFix(fix as never),
    };
  });
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root not found');

createRoot(rootEl).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
